import DimensionsService from './dimensionsService';
import plotsService from './plotsService';
import { getRumNameFromResource } from '@splunk/olly-imm/build/detectors/utils/rumFunctions';
export const APM_IDENTIFIERS = ['ep_above_ft', 'd_above_ft', 'signal_baseline'];
export const APM_ALERT_TYPES = ['errors', 'latency', 'requests'];
import { capitalize } from 'lodash';
import { SCALING_UNIT_TYPES } from '../../../../common/ui/formatting/scalingUnitTypes';
import { formatWithUnit } from './unitFormatUtils';

export default class ChartInformationService {
    constructor(detectorPriorityService, valueFormatter, signalTypeService, featureEnabled) {
        this.detectorPriorityService = detectorPriorityService;
        this.valueFormatter = valueFormatter;
        this.signalTypeService = signalTypeService;
        this.featureEnabled = featureEnabled;
        this.dimensionsService = new DimensionsService(featureEnabled);
    }

    getChartInformation(event, incidentInfo, orgId, plotColorMap, customUnitType, customUnit) {
        const incidentRelatedPlots = plotsService.getPlotsForIncident(event, incidentInfo);
        const triggerValue = plotsService.getTriggerValue(incidentRelatedPlots);
        const unitType = this.featureEnabled('alertModalShowUnits')
            ? this.getUnitType(event, customUnitType)
            : null;
        const annotationCustomUnit = this.featureEnabled('alertModalShowUnits')
            ? this.getAnnotationCustomUnit(event)
            : null;
        const effectiveCustomUnit =
            annotationCustomUnit || (!unitType && customUnit ? customUnit : null);
        return {
            swatchClass: this._getSwatchClass(event.metadata.sf_severity),
            title: this.buildChartTitle(
                event,
                incidentInfo,
                incidentRelatedPlots,
                unitType,
                effectiveCustomUnit
            ),
            plots: plotsService.getPlotsInformation(
                incidentRelatedPlots,
                event,
                this.valueFormatter.formatValue,
                plotColorMap
            ),
            condition: event.metadata.sf_detectOnCondition,
            triggerValue: triggerValue,
            dimensions: this.dimensionsService.getForPlots(incidentRelatedPlots, orgId),
            unitType: unitType,
            customUnit: effectiveCustomUnit,
        };
    }

    buildChartTitle(event, incidentInfo, relatedPlots, unitType, customUnit) {
        if (event?.properties?.detector_type === 'rum') {
            return this.buildForRUMDetector(event, relatedPlots, unitType, customUnit);
        }

        if (event?.metadata?.sf_eventSloId) {
            return this.buildForSlo(event, relatedPlots, unitType, customUnit);
        }

        const isAPMTypeDetector =
            event?.properties?.detector_type === 'apm' &&
            APM_ALERT_TYPES.includes(event?.properties?.alert_type);
        // hasAPMIdentifiers can be removed after APM detectors migration
        const hasAPMIdentifiers =
            incidentInfo && APM_IDENTIFIERS.some((id) => id === incidentInfo.conditionIdentifier);
        if (isAPMTypeDetector || hasAPMIdentifiers) {
            return this.buildForAPMDetector(event, relatedPlots, unitType, customUnit);
        }
        return this.buildForInfrastructureDetector(
            relatedPlots,
            incidentInfo,
            unitType,
            customUnit
        );
    }

    getValueString(value, threshold, unitType, customUnit, comparator = '>') {
        if ((value || value === 0) && (threshold || threshold === 0)) {
            return `(value: ${formatWithUnit(
                value,
                unitType,
                customUnit
            )}) ${comparator} ${formatWithUnit(threshold, unitType, customUnit)}`;
        }
        return '';
    }

    buildForSlo(event, relatedPlots, unitType, customUnit) {
        function getSloAlertTypeFromAlertName(alertType) {
            switch (alertType) {
                case 'BREACH':
                    return 'Breach';
                case 'ERROR_BUDGET_LEFT':
                    return 'Error Budget Left';
                case 'BURN_RATE':
                    return 'Burn Rate';
                default:
                    return `${capitalize(alertType)}`;
            }
        }
        const value = plotsService.getTriggerValue(relatedPlots);
        const threshold = plotsService.getSloThresholdValue(relatedPlots);
        const sloAlertType = getSloAlertTypeFromAlertName(event.metadata.sf_eventSloAlertType);
        const valueString = this.getValueString(value, threshold, unitType, customUnit, '<');
        return `SLO ${sloAlertType} Event ${valueString}`;
    }

    buildForAPMDetector(event, relatedPlots, unitType, customUnit) {
        const service = event.metadata[this.signalTypeService.getAPMDimensions().service];
        const value = plotsService.getTriggerValue(relatedPlots);
        const threshold = plotsService.getThresholdValue(relatedPlots);
        const environment = event.metadata[this.signalTypeService.getAPMDimensions().environment];
        const workflow = event.metadata[this.signalTypeService.getAPMDimensions().workflow];
        const useTransactionTerminology = this.featureEnabled(
            'apm2RenameWorkflowToTransactionInUI'
        );
        const workflowLabel = useTransactionTerminology ? 'Business Transaction' : 'Workflow';

        const comparator = plotsService.getThresholdDirection(event.metadata.sf_detectOnCondition);

        const alertTypeLabels = {
            latency: 'Latency',
            requests: 'Requests',
            errors: 'Error rate',
        };

        const buildTitle = (type, alertType, service, environment, value, threshold) => {
            const label = alertTypeLabels[alertType]
                ? `APM ${type} ${alertTypeLabels[alertType]}`
                : `APM ${type}`;

            return `${label} - ${service}:${environment} ${this.getValueString(
                value,
                threshold,
                unitType,
                customUnit,
                comparator
            )}`;
        };

        if (workflow) {
            return buildTitle(
                workflowLabel,
                event.properties.alert_type,
                workflow,
                environment,
                value,
                threshold
            );
        }
        return buildTitle(
            'Service',
            event.properties.alert_type,
            service,
            environment,
            value,
            threshold
        );
    }

    buildForRUMDetector(event, relatedPlots, unitType, customUnit) {
        const value = plotsService.getTriggerValue(relatedPlots);
        const threshold = plotsService.getThresholdValue(relatedPlots);
        const comparator = plotsService.getThresholdDirection(event.metadata.sf_detectOnCondition);
        const rumFunctionLabel = getRumNameFromResource(
            event.properties.resource_type,
            event.properties.alert_type
        );
        return `RUM ${rumFunctionLabel} ${this.getValueString(
            value,
            threshold,
            unitType,
            customUnit,
            comparator
        )}`;
    }

    buildForInfrastructureDetector(relatedPlots, incidentInfo, unitType, customUnit) {
        if (incidentInfo && incidentInfo.builtInFunction && incidentInfo.readableCondition) {
            return incidentInfo.readableCondition;
        }

        const metricName = plotsService.getTriggerPlotName(relatedPlots);
        let value = plotsService.getTriggerValue(relatedPlots);
        if (value === '-') {
            value = 'N/A';
        }

        if (!metricName || (!value && value !== 0)) {
            return '';
        }

        return `${metricName} (value: ${formatWithUnit(value, unitType, customUnit)})`;
    }

    getUnitType(event, customUnitType) {
        const annotationUnit = this.getAnnotationUnitConfig(event);
        if (annotationUnit) {
            if (annotationUnit.unitType) {
                return annotationUnit.unitType;
            }
            if (annotationUnit.customUnit) {
                return undefined;
            }
        }
        // APM, RUM and SLO Breach have inherent unit types that always apply.
        const detectorConfig = this.getDetectorChartUnitConfig(event);
        if (detectorConfig && detectorConfig.unitType) {
            return detectorConfig.unitType;
        }
        // Custom / infrastructure detector units are gated by the feature flag.
        if (!this.featureEnabled('alertModalShowUnits')) {
            return undefined;
        }
        if (customUnitType) {
            return customUnitType;
        }
        return undefined;
    }

    getAnnotationCustomUnit(event) {
        const config = this.getAnnotationUnitConfig(event);
        return config?.customUnit || null;
    }

    getAnnotationUnitConfig(event) {
        const unit = event?.properties?.unit;
        if (unit) {
            const isKnownScalingUnit = Object.values(SCALING_UNIT_TYPES).includes(unit);
            if (isKnownScalingUnit) {
                return { unitType: unit };
            }
            return { customUnit: { prefix: '', suffix: unit } };
        }
        const prefix = event?.properties?.unitPrefix;
        const suffix = event?.properties?.unitSuffix;
        if (prefix || suffix) {
            return { customUnit: { prefix: prefix || '', suffix: suffix || '' } };
        }
        return null;
    }

    _getSwatchClass(severityName) {
        const severity = this.detectorPriorityService.getSeverityByDisplay(severityName);

        return this.detectorPriorityService.getSwatchClassBySeverity(severity);
    }

    /**
     * Returns unit configuration for known detector types (APM, RUM, SLO Breach).
     * Returns null when the unit type cannot be determined from the event alone
     * (e.g. custom detectors that need a vizOptions API call).
     */
    getDetectorChartUnitConfig(event) {
        // APM detectors
        const isAPMTypeDetector =
            event?.properties?.detector_type === 'apm' &&
            APM_ALERT_TYPES.includes(event?.properties?.alert_type);

        if (isAPMTypeDetector) {
            switch (event.properties.alert_type) {
                case 'latency':
                    return { unitType: SCALING_UNIT_TYPES.MILLISECOND };
                case 'errors':
                    return { unitType: SCALING_UNIT_TYPES.PERCENT };
                case 'requests':
                    return { unitType: SCALING_UNIT_TYPES.REQUESTS_PER_SECOND };
                default:
                    return null;
            }
        }

        // RUM detectors
        if (event?.properties?.detector_type === 'rum') {
            switch (event.properties.alert_type) {
                case 'latency':
                    return { unitType: SCALING_UNIT_TYPES.MILLISECOND };
                case 'error_rate':
                    return { unitType: SCALING_UNIT_TYPES.PERCENT };
                default:
                    return null;
            }
        }

        // SLO Breach events
        if (event?.metadata?.sf_eventSloAlertType === 'BREACH') {
            return { unitType: SCALING_UNIT_TYPES.PERCENT };
        }

        return null;
    }
}
